import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import {Icon} from '@components/common/base/icon'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {cn} from '@containers/html-utils'
import {TwMenuContainer, TwMenuItem, TwTitle} from './containers'
import {Menu} from './menu'
import {MenuInstance, MenuItemInstance, MenuItemProps} from './types'

function renderCustomSubmenu(
  customDropdown: React.ReactNode | ((close: () => void) => React.ReactNode),
  close: () => void
) {
  return typeof customDropdown === 'function'
    ? customDropdown(close)
    : customDropdown
}

export const MenuItem = forwardRef<MenuItemInstance, MenuItemProps>(
  (props: MenuItemProps, forwardedRef) => {
    const {id, icon, iconColor, color, title, onClick} = props
    const {
      className,
      disabled,
      customDropdown: customDropdown,
      items,
      onHover,
      onItemClicked
    } = props
    const {popupMenu, tooltip, after} = props

    const ref = useRef<HTMLDivElement>(null)
    const submenuRef = useRef<MenuInstance>(null)
    const [visible, setVisible] = useState(false)
    const open = useCallback(() => setVisible(true), [setVisible])
    const close = useCallback(() => {
      submenuRef.current?.close()
      setVisible(false)
    }, [setVisible, submenuRef])
    useImperativeHandle(forwardedRef, () => ({close}), [close])
    const hover = useCallback(() => {
      ;(customDropdown || items?.length) && !disabled && open()
      onHover?.(id)
    }, [items?.length, customDropdown, disabled, open, onHover, id, submenuRef])
    const click = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        if (!disabled && onClick) {
          onClick(e)
          onItemClicked?.()
        }
      },
      [onClick, onItemClicked, disabled]
    )

    const TooltipContainer = tooltip ? Tooltip : React.Fragment

    const submenu = (!!customDropdown || !!items?.length) &&
      !disabled &&
      visible && (
        <Dropdown
          reference={ref}
          placement="right-start"
          fallbackPlacements={['right-end', 'left-start', 'left-end']}
          visible
          hideArrow
          hideOverlay
          content={
            items?.length ? (
              <Menu
                ref={submenuRef}
                items={items}
                onItemClicked={onItemClicked}
              />
            ) : (
              <TwMenuContainer>
                {renderCustomSubmenu(customDropdown, onItemClicked)}
              </TwMenuContainer>
            )
          }
          onVisibleChange={close}
          theme="new-menu"
        />
      )

    return (
      <React.Fragment>
        <TooltipContainer
          {...(tooltip ? {content: tooltip, placement: 'bottom'} : undefined)}
        >
          <TwMenuItem
            ref={ref}
            className={cn(className, 'group/menu-item')}
            $disabled={disabled}
            $forceHover={visible}
            $popupMenu={popupMenu}
            onClick={click}
            onMouseOver={hover}
          >
            {typeof icon === 'string' ? (
              <Icon
                icon={icon}
                color={disabled ? 'disabled-new' : iconColor ?? color}
              />
            ) : (
              icon
            )}
            <TwTitle $disabled={disabled} $color={color}>
              {title}
            </TwTitle>
            {after}
            {(!!customDropdown || !!items?.length) && (
              <Icon
                icon="IconChevronRight"
                color={'secondary'}
                className="ml-auto"
              />
            )}
            {popupMenu && (
              <Icon
                icon="IconChevronRight"
                color={disabled ? 'disabled' : 'secondary'}
                className="ml-auto invisible group-hover:visible"
              />
            )}
          </TwMenuItem>
        </TooltipContainer>
        {submenu}
      </React.Fragment>
    )
  }
)
